// Building a Command from what the user tapped. The phone checks the same things the daemon will
// refuse (docs/protocol.md, "Rules the daemon enforces"), so a refused answer is never a surprise.
import { MAX_TEXT_CHARS, randomId, type Card, type Choice, type Command, type Stamp } from "@whistle/protocol";

export type CommandErrorCode = "no-choice" | "text-not-allowed" | "text-too-long" | "text-control-chars" | "missing-answer" | "bad-option" | "too-many" | "other-not-allowed" | "not-answerable";

export class CommandError extends Error {
  readonly code: CommandErrorCode;
  constructor(code: CommandErrorCode, message: string) {
    super(message);
    this.code = code;
    this.name = "CommandError";
  }
}

/** Control characters other than newline are refused; CRLF is folded to LF first. */
export function normaliseText(text: string): string {
  return text.replace(/\r\n?/g, "\n");
}

export function checkText(text: string): string {
  const t = normaliseText(text);
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u0009\u000B-\u001F\u007F]/.test(t)) throw new CommandError("text-control-chars", "The text contains characters that can't be sent.");
  if ([...t].length > MAX_TEXT_CHARS) throw new CommandError("text-too-long", `Replies are at most ${MAX_TEXT_CHARS.toLocaleString()} characters.`);
  return t;
}

export function remainingChars(text: string): number {
  return MAX_TEXT_CHARS - [...normaliseText(text)].length;
}

/** Choices shown on the card: risky ones only when the machine shows them (the daemon omits them otherwise, but never trust a stray flag). */
export function visibleChoices(card: Card): Choice[] {
  return card.choices.filter((c) => !c.risky);
}

export function buildChoiceCommand(card: Card, stamp: Stamp, choiceId: string, text?: string): Command {
  if (card.kind !== "approval" && card.kind !== "question") throw new CommandError("not-answerable", "This one needs your desk.");
  const choice = card.choices.find((c) => c.id === choiceId);
  if (!choice || choice.risky) throw new CommandError("no-choice", "That choice isn't offered any more.");
  const action: Command["action"] = { kind: "choice", choiceId };
  if (text !== undefined && text.trim() !== "") {
    if (!card.allowsText) throw new CommandError("text-not-allowed", "Typed replies are off on this machine.");
    if (choice.effect !== "deny") throw new CommandError("text-not-allowed", "An explanation can only go with a deny.");
    action.text = checkText(text);
  }
  return { commandId: randomId(), requestId: card.requestId, stamp, promptHash: card.promptHash, action };
}

export type AnswerSet = Record<string, string[]>;
export type OtherSet = Record<string, string>;

/** Validates a complete answer set against the card's questions: every question answered, counts right, option ids real. */
export function buildAnswersCommand(card: Card, stamp: Stamp, answers: AnswerSet, other?: OtherSet): Command {
  if (card.kind !== "question" || !card.questions?.length) throw new CommandError("not-answerable", "This card has no questions to answer.");
  const cleanAnswers: AnswerSet = {};
  const cleanOther: OtherSet = {};
  for (const q of card.questions) {
    const picked = answers[q.id] ?? [];
    const otherText = other?.[q.id];
    const hasOther = otherText !== undefined && otherText.trim() !== "";
    if (hasOther && !card.allowsText) throw new CommandError("other-not-allowed", "Typed answers are off on this machine.");
    for (const id of picked) if (!q.options.some((o) => o.id === id)) throw new CommandError("bad-option", `"${id}" isn't one of the options.`);
    if (picked.length === 0 && !hasOther) throw new CommandError("missing-answer", `Answer "${q.header ?? q.text}" first.`);
    if (!q.multiSelect && picked.length > 1) throw new CommandError("too-many", "Pick one option for this question.");
    cleanAnswers[q.id] = [...new Set(picked)];
    if (hasOther) cleanOther[q.id] = checkText(otherText);
  }
  for (const id of Object.keys(answers)) if (!card.questions.some((q) => q.id === id)) throw new CommandError("bad-option", "An answer refers to a question that isn't on the card.");
  const action: Command["action"] = { kind: "answers", answers: cleanAnswers };
  if (Object.keys(cleanOther).length) action.other = cleanOther;
  return { commandId: randomId(), requestId: card.requestId, stamp, promptHash: card.promptHash, action };
}

/** Short human summary for Recent and the hold banner: "Allow once", "Deny and explain", "2 answers". */
export function summariseCommand(command: Command, card: Card): string {
  if (command.action.kind === "choice") {
    const choice = card.choices.find((c) => c.id === (command.action as { choiceId: string }).choiceId);
    return choice?.label ?? "Answer";
  }
  const n = Object.keys(command.action.answers).length;
  return n === 1 ? "1 answer" : `${n} answers`;
}

/** Whether the answer set is complete enough to submit (drives the button state before any command is built). */
export function answersComplete(card: Card, answers: AnswerSet, other?: OtherSet): boolean {
  if (!card.questions?.length) return false;
  return card.questions.every((q) => (answers[q.id]?.length ?? 0) > 0 || (card.allowsText && (other?.[q.id]?.trim() ?? "") !== ""));
}
